/**
 * PDP add-to-cart reliability + ATC loader
 *
 * Legacy ProductForm reads selling_plan from the URL query string, not the form input.
 * Recharge updates the hidden input, but the URL can lag or stay stale after variant /
 * purchase-type changes. That yields intermittent 422s: "Cannot apply selling plan to variant".
 *
 * Sync the URL from the form on ATC click (capture phase), keep it synced with Recharge,
 * show a button loader while adding, and open SmartCart after cartChanged.
 */
(function () {
  const ATC_SELECTOR =
    '.product-form__submit, .product-form-sticky-banner__actions__submit, [data-ref="submitButton"]';
  let loadingButtons = [];
  let loadingTimeout = null;

  function injectLoaderStyles() {
    if (document.getElementById('pdp-atc-loader-styles')) return;

    const style = document.createElement('style');
    style.id = 'pdp-atc-loader-styles';
    style.textContent = `
      .product-form__submit.is-loading,
      .product-form-sticky-banner__actions__submit.is-loading {
        position: relative;
        pointer-events: none;
        cursor: wait;
      }

      .product-form__submit.is-loading .product-form__submit-inner,
      .product-form-sticky-banner__actions__submit.is-loading .product-form-sticky-banner__submit-inner,
      .product-form-sticky-banner__actions__submit.is-loading .product-form-sticky-banner__actions__submit__available {
        opacity: 0;
      }

      .product-form__submit.is-loading::after,
      .product-form-sticky-banner__actions__submit.is-loading::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 20px;
        height: 20px;
        margin: -10px 0 0 -10px;
        border: 2px solid rgba(255, 255, 255, 0.35);
        border-top-color: #fff;
        border-radius: 50%;
        animation: pdp-atc-spin 0.7s linear infinite;
      }

      @keyframes pdp-atc-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  function getAtcButtons() {
    return Array.prototype.slice.call(document.querySelectorAll(ATC_SELECTOR));
  }

  function setAtcLoading(isLoading) {
    injectLoaderStyles();
    const buttons = getAtcButtons();

    buttons.forEach(function (button) {
      button.classList.toggle('is-loading', isLoading);
      button.disabled = isLoading;
      button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    });

    loadingButtons = isLoading ? buttons : [];

    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
      loadingTimeout = null;
    }

    // Safety clear if cart never responds
    if (isLoading) {
      loadingTimeout = setTimeout(function () {
        setAtcLoading(false);
      }, 10000);
    }
  }

  function getProductForm() {
    return document.querySelector('form.shopify-product-form');
  }

  function syncSellingPlanUrlFromForm(form) {
    form = form || getProductForm();
    if (!form) return;

    const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    const variantInput =
      form.querySelector('input[data-ref="variantIdInput"]') || form.querySelector('input[name="id"]');
    const url = new URL(window.location.href);
    let changed = false;

    if (variantInput && variantInput.value) {
      if (url.searchParams.get('variant') !== String(variantInput.value)) {
        url.searchParams.set('variant', String(variantInput.value));
        changed = true;
      }
    }

    const planValue = sellingPlanInput && sellingPlanInput.value ? String(sellingPlanInput.value) : '';
    if (planValue) {
      if (url.searchParams.get('selling_plan') !== planValue) {
        url.searchParams.set('selling_plan', planValue);
        changed = true;
      }
    } else if (url.searchParams.has('selling_plan')) {
      url.searchParams.delete('selling_plan');
      changed = true;
    }

    if (changed) {
      window.history.replaceState({}, '', url);
    }
  }

  // Run before ProductForm's click handler reads window.location
  document.addEventListener(
    'click',
    function (event) {
      const button = event.target.closest(ATC_SELECTOR);
      if (!button || button.disabled || button.classList.contains('is-loading')) return;
      if (button.classList.contains('product-form__submit--disabled')) return;
      if (button.classList.contains('product-form-sticky-banner__actions__submit--disabled')) return;

      const form =
        button.closest('form.shopify-product-form') ||
        (button.closest('product-form') && button.closest('product-form').querySelector('form.shopify-product-form')) ||
        getProductForm();

      syncSellingPlanUrlFromForm(form);
      setAtcLoading(true);
    },
    true
  );

  document.addEventListener('cartChanged', function () {
    setAtcLoading(false);
  });

  document.addEventListener('cart:updated', function () {
    setAtcLoading(false);
  });

  // Clear loader when ProductForm surfaces an error
  function watchProductFormErrors() {
    const errorEl = document.querySelector('.product-form__error');
    if (!errorEl) return;

    new MutationObserver(function () {
      if (errorEl.classList.contains('product-form__error--active')) {
        setAtcLoading(false);
      }
    }).observe(errorEl, { attributes: true, attributeFilter: ['class'] });
  }

  function bindRechargeReady(api) {
    if (!api || !api.events) return;

    const syncFromApi = function (variantId, planId) {
      const url = new URL(window.location.href);
      if (variantId) url.searchParams.set('variant', String(variantId));
      if (planId) url.searchParams.set('selling_plan', String(planId));
      else url.searchParams.delete('selling_plan');
      window.history.replaceState({}, '', url);
    };

    if (typeof api.events.onSellingPlanChange === 'function') {
      api.events.onSellingPlanChange(function (planId) {
        syncFromApi(api.variantId, planId || null);
        // Keep form input as source of truth shortly after Recharge mutates it
        setTimeout(function () {
          syncSellingPlanUrlFromForm();
        }, 0);
      });
    }

    if (typeof api.events.onVariantChange === 'function') {
      api.events.onVariantChange(function (variantId) {
        setTimeout(function () {
          const form = getProductForm();
          const sp = form && form.querySelector('input[name="selling_plan"]');
          syncFromApi(variantId, sp && sp.value ? sp.value : null);
        }, 0);
      });
    }

    syncFromApi(api.variantId, api.sellingPlanId || null);
    syncSellingPlanUrlFromForm();
  }

  // Chain with any existing callback (including the Liquid one) and always attach listeners
  const previousReady = window.RechargeSubscriptionWidgetReady;
  window.RechargeSubscriptionWidgetReady = function (api) {
    if (typeof previousReady === 'function') {
      try {
        previousReady(api);
      } catch (error) {
        console.error('RechargeSubscriptionWidgetReady error:', error);
      }
    }
    bindRechargeReady(api);
  };

  function onReady() {
    injectLoaderStyles();
    syncSellingPlanUrlFromForm();
    watchProductFormErrors();
  }

  // If Recharge already booted before this file, sync from the form now
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
